const LEGACY_TYPE_MAP = {
  material_struct_parent_uuid: 'mtr_struct_main_id',
  material_struct_uuid: 'mtr_struct_sub_id',
};

function createItemId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `sf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function normalizeSerialFormatterType(type = '') {
  return LEGACY_TYPE_MAP[type] ?? type;
}

export function parseSerialFormatter(raw) {
  if (!raw) {
    return [];
  }

  let parsed = raw;

  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.map((item) => ({
    id: item.id ?? createItemId(),
    type: normalizeSerialFormatterType(item.type ?? 'custom'),
    value: item.value ?? '',
    label: item.label ?? String(item.value ?? ''),
  }));
}

export function serializeSerialFormatter(items = []) {
  const payload = items.map(({ type, value, label }) => ({
    type,
    value,
    label,
  }));

  return JSON.stringify(payload);
}

export function formatSerialFormatterForDisplay(raw) {
  const items = parseSerialFormatter(raw);

  if (items.length === 0) {
    return '-';
  }

  return items.map((item) => item.label).join(' → ');
}

export function createSerialFormatterItem(type, value, label) {
  return {
    id: createItemId(),
    type,
    value,
    label,
  };
}

export function isSerialFormatterSourceUsed(items = [], source) {
  if (!source?.type) {
    return false;
  }

  return items.some((item) => {
    if (item.type !== source.type) {
      return false;
    }

    if (item.type === 'mtr_struct_main_id' || item.type === 'mtr_struct_sub_id') {
      return true;
    }

    return item.value === source.value;
  });
}

/** 序號段 type：指向模板 specs 的 mtrSpecId（含 legacy） */
export const SPEC_LINKED_SERIAL_TYPES = new Set(['mtr_spec', 'spec_enum', 'spec_value']);

/**
 * specs 尚無任何 mtrSpecId、但 items 仍有規格段 → 視為尚未 hydrate，勿 prune。
 * （edit 首載 defaultValues.specs=[] 不是「使用者清空全部規格」）
 */
export function shouldDeferPruneSerialFormatter(specs = [], items = []) {
  const hasSelectedSpecId = (Array.isArray(specs) ? specs : []).some(
    (rule) => Boolean(rule?.mtrSpecId),
  );
  if (hasSelectedSpecId) {
    return false;
  }

  return (Array.isArray(items) ? items : []).some(
    (item) => SPEC_LINKED_SERIAL_TYPES.has(item?.type),
  );
}

/**
 * 剔除仍指向已不在 specs 內之 mtrSpecId 的序號段。
 * custom／manufacture／主／次分類段保留。
 *
 * @param {{ type?: string, value?: string }[]} items
 * @param {Iterable<string>|Set<string>} selectedSpecIds — 目前 specs 的 mtrSpecId
 * @returns {typeof items}
 */
export function pruneSerialFormatterItemsBySpecIds(items = [], selectedSpecIds = []) {
  const allowedIds = selectedSpecIds instanceof Set
    ? selectedSpecIds
    : new Set(
      [...(selectedSpecIds ?? [])]
        .map((id) => String(id ?? ''))
        .filter(Boolean),
    );

  return (Array.isArray(items) ? items : []).filter((item) => {
    const type = item?.type ?? '';
    if (!SPEC_LINKED_SERIAL_TYPES.has(type)) {
      return true;
    }

    const value = String(item?.value ?? '');
    return Boolean(value) && allowedIds.has(value);
  });
}
